<!-- eslint-disable vue/no-deprecated-slot-attribute -->
<template>
  <div ref="videoContainer" class="w-full bg-black">
    <div class="relative flex w-full items-center justify-center">
      <div class="mux-player flex w-full items-center justify-center bg-black">
        <VideoError v-if="hasError" />
        <template v-else>
          <media-controller ref="mediaController" class="relative h-full w-full">
            <!-- Mux video as the media -->
            <mux-video
              :class="isLoading ? 'hidden' : ''"
              class="mux-player h-full w-full"
              ref="muxPlayer"
              slot="media"
              @click="handleVideoClick"
              :src="signedSrc"
              :thumbnail-token="thumbnailToken"
              loop
              :metadata-video-id="id"
              :metadata-video-title="title"
              :metadata-viewer-user-id="fanStore.fanId"
              metadata-sub-property-id="sparklejumpropequeen"
              :muted="isMuted"
              no-volume-pref="true"
              stream-type="on-demand"
              prefer-playback="mse"
              :debug="muxDebug"
              :env-key="muxEnvKey"
              playsinline
              loading="lazy"
              @error="handleError"
              @ended="handleVideoEnded"
              @loadeddata="handleLoadedData"
              @volumechange="handleVolumeChange"
            ></mux-video>

            <!-- Mute button for normal view (always visible) -->
            <media-mute-button
              v-if="!isFullscreenView"
              data-fullscreen="false"
              class="pointer-events-auto absolute bottom-4 right-4 z-50 flex h-9 w-9 items-center justify-center rounded-full border-0 bg-black/50 p-0 text-white opacity-100 hover:bg-black/70"
            ></media-mute-button>

            <!-- Mute button for fullscreen (with auto-hide) -->
            <media-control-bar
              v-if="isFullscreenView"
              class="pointer-events-auto absolute left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2"
            >
              <media-mute-button
                data-fullscreen="true"
                class="flex h-16 w-16 items-center justify-center rounded-full border-0 bg-black/60 p-0 text-white opacity-100 shadow-lg hover:bg-black/80"
              ></media-mute-button>
            </media-control-bar>

            <!-- Fullscreen-only play button (with auto-hide) -->
            <media-control-bar
              v-if="isFullscreenView"
              class="pointer-events-auto absolute bottom-4 left-4 z-50"
            >
              <media-play-button
                data-fullscreen="true"
                class="grid h-12 w-12 place-items-center rounded-full border-0 bg-black/60 text-white opacity-100 shadow-lg hover:bg-black/80"
              ></media-play-button>
            </media-control-bar>

            <media-play-button
              v-if="!isFullscreenView && !isLoading"
              data-fullscreen="false"
              class="pointer-events-auto absolute bottom-4 left-4 z-50 flex h-9 w-9 items-center justify-center rounded-full border-0 bg-black/50 p-0 text-white opacity-100 hover:bg-black/70"
            ></media-play-button>

            <!-- Fullscreen-only back button (exit fullscreen) -->
            <button
              v-if="isFullscreenView"
              @click.stop.prevent="closeFullscreen"
              class="pointer-events-auto fixed left-4 top-4 z-50 grid h-12 w-12 place-items-center rounded-full border-0 bg-black/60 text-white opacity-100 shadow-lg hover:bg-black/80"
              aria-label="Exit fullscreen"
            >
              <MoveLeft stroke="white" />
            </button>

            <!-- Fullscreen-only timeline scrubber -->
            <media-time-range
              v-if="isFullscreenView"
              class="scrubber pointer-events-auto fixed bottom-0 z-50 h-1"
            ></media-time-range>
          </media-controller>
        </template>
        <VideoLoader v-if="isLoading" :ratio="attrs.aspectRatio" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import '@mux/mux-player';
import 'media-chrome';
import 'media-chrome/media-theme-element';
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useFanStore } from '@stores/fan.store';
import { useVideoPlayerStore } from '@/stores/videoPlayer.store';
import { useTelemetryFetcher } from '@api/tracking.api';
import { useI18n } from 'vue-i18n';
import { getVideoAttrs } from '../extensions/Video/VideoExtension';
import { useMuxMediaFetcher } from '@api/content.api';
import { useAccountStore } from '@stores/account.store';
import { storeToRefs } from 'pinia';
import { MoveLeft } from 'lucide-vue-next';

import type { NodeProps } from './RenderNode.vue';
import type { PlaybackData } from '@interfaces/media.interface';

import VideoError from '@modules/VideoPlayer/VideoError.vue';
import VideoLoader from '@modules/VideoPlayer/VideoLoader.vue';

const muxDebug = false;
const muxEnvKey = import.meta.env.VITE_MUX_ENV_KEY;

interface MuxPlayerElement extends HTMLVideoElement {
  webkitEnterFullscreen?: () => Promise<void>;
  webkitExitFullscreen?: () => Promise<void>;
  media: HTMLVideoElement & {
    webkitEnterFullscreen?: () => Promise<void>;
    webkitExitFullscreen?: () => Promise<void>;
  };
  paused: boolean;
  muted: boolean;
  volume: number;
}

interface DocumentWithWebkit extends Document {
  webkitFullscreenElement: Element | null;
}

const props = defineProps<NodeProps>();

// Stores
const fanStore = useFanStore();
const videoPlayerStore = useVideoPlayerStore();
const accountStore = useAccountStore();

// Composables
const { t } = useI18n();
const postTelemetry = useTelemetryFetcher();
const videoContentFetcher = useMuxMediaFetcher();

// Refs
const muxPlayer = ref<MuxPlayerElement | null>(null);
const mediaController = ref<HTMLElement | null>(null);
const videoError = ref<string | null>(null);
const contentViewCompleted = ref(false); // for telemetry
const contentViewStarted = ref(false); // for telemetry
const playbackData = ref<PlaybackData>();
const isPlayerReady = ref(false);
const { isMuted, allowFullscreenOnRotate } = storeToRefs(videoPlayerStore);
const isFullscreenView = ref(false);

// Computed
const signedSrc = computed(() => {
  if (!playbackId.value || !token.value) return '';
  return `https://stream.mux.com/${playbackId.value}.m3u8?token=${token.value}`;
});
const attrs = computed(() => getVideoAttrs(props.node.attrs));
const hasError = computed(() => !!videoError.value);
const isLoading = computed(() => !hasError.value && !isPlaying.value && !isPlayerReady.value);
const isPlaying = computed(
  () => !!muxPlayer.value && isPlayerReady.value && !muxPlayer.value.paused,
);

// Video player props from attrs
const playbackId = computed(() => playbackData.value?.playbackId || '');
const token = computed(() => playbackData.value?.token || '');
const thumbnailToken = computed(() => playbackData.value?.thumbnailToken || '');
const id = computed(() => attrs.value.contentId || '');
const title = computed(() => attrs.value.title || '');
const isActive = computed(() => props.isActive || false);

// Video Player event handlers
const handleError = (event: Event) => {
  const video = event.target as HTMLVideoElement;
  videoError.value = video?.error?.message || t('errors.couldNotLoadContent');
};

const handleVideoEnded = () => {
  if (!muxPlayer.value) return;
  if (!contentViewCompleted.value) {
    contentViewCompleted.value = true;
    postTelemetry.execute({
      metric: 'view-post-video-complete',
      resource: `${window.location.href}?postId=${id.value}`,
    });
  }
  // Restart the video
  muxPlayer.value.currentTime = 0;
  muxPlayer.value.play();
};

const handleLoadedData = () => {
  isPlayerReady.value = true;
  if (isActive.value) play();
  recordViewTelemetry();
};

const handleVolumeChange = () => {
  if (!muxPlayer.value) return;
  isMuted.value = muxPlayer.value.muted;
};

// Video Player methods
const play = () => {
  if (muxPlayer.value) muxPlayer.value.play();
};

const pause = () => {
  if (muxPlayer.value) muxPlayer.value.pause();
};

const toggleFullscreen = async () => {
  const mux = muxPlayer.value;
  if (!mux) return;

  // iOS Safari fallback
  if (mux.webkitEnterFullscreen) {
    try {
      await mux.webkitEnterFullscreen();
      return;
    } catch (error) {
      console.log('webkitEnterFullscreen failed:', error);
    }
  }

  // Standard fullscreen API fallback
  try {
    if (!document.fullscreenElement && mediaController.value?.requestFullscreen) {
      await mediaController.value.requestFullscreen();
      return;
    }
    if (!document.fullscreenElement && mux.requestFullscreen) {
      await mux.requestFullscreen();
      return;
    }
  } catch (error) {
    console.log('standard fullscreen failed:', error);
  }
};

const recordViewTelemetry = () => {
  if (!contentViewStarted.value) {
    contentViewStarted.value = true;
    postTelemetry.execute({
      metric: 'view-post-video',
      resource: `${window.location.href}?postId=${id.value}`,
    });
  }
};

watch(
  () => isActive.value,
  (isActive) => {
    if (isActive) play();
    else pause();
  },
  { immediate: true },
);

// Watch for fullscreen on rotate request from store
watch(
  () => allowFullscreenOnRotate.value,
  (shouldFullscreen) => {
    if (shouldFullscreen && muxPlayer.value && isPlayerReady.value) {
      toggleFullscreen();
    }
  },
);

const handleFullscreenChange = () => {
  const isFullscreen =
    !!document.fullscreenElement || !!(document as DocumentWithWebkit).webkitFullscreenElement;

  isFullscreenView.value = isFullscreen;
};

const handleVideoClick = async (event: Event) => {
  event.stopPropagation();
  event.preventDefault();
  if (!muxPlayer.value) return;

  if (isFullscreenView.value) {
    toggleMute();
    return;
  }

  await toggleFullscreen();
};

const toggleMute = () => {
  if (!muxPlayer.value) return;
  muxPlayer.value.muted = !muxPlayer.value.muted;
  isMuted.value = muxPlayer.value.muted;
};

const closeFullscreen = async () => {
  if (document.fullscreenElement && document.exitFullscreen) {
    await document.exitFullscreen();
  }
};

onMounted(async () => {
  // Load playback data
  await videoContentFetcher.execute(
    { id: attrs.value.contentId, thumbnailTime: 0 },
    accountStore.authToken,
  );
  playbackData.value = videoContentFetcher.data.value;

  // Set up event listeners
  document.addEventListener('fullscreenchange', handleFullscreenChange);
  document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
});

onUnmounted(() => {
  document.removeEventListener('fullscreenchange', handleFullscreenChange);
  document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);

  // Remove click handler from mux-video element
  if (muxPlayer.value?.shadowRoot) {
    const muxVideo = muxPlayer.value.shadowRoot.querySelector('mux-video');
    if (muxVideo) {
      muxVideo.removeEventListener('click', handleVideoClick as EventListener);
    }
  }
});
</script>

<style scoped lang="postcss">
.mux-player {
  aspect-ratio: v-bind('attrs.aspectRatio');
}
</style>
